import { ApolloProvider } from '@apollo/client';
import { CKIconGradient } from '@clearkit/icons';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import { init } from 'commandbar';
import queryString from 'query-string';
import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import { Provider } from 'unstated';
import UNSTATED from 'unstated-debug';
// Initialize commandbar
init('1ee489c0');

UNSTATED.enabled = process.env.NODE_ENV === 'development';
UNSTATED.logStateChanges = false;

import MissingPage from '~/components/MissingPage';
import NPS from '~/components/NPS';
import SegmentsMenu from '~/components/SegmentsMenu';
import SidebarDrawer from '~/components/SidebarDrawer';
import ToastStack from '~/components/ToastStack';
import apollo from '~/lib/apollo';
import { integrationTypes } from '~/lib/dataEnrichment';
import {
  userHasAudiencesAccess,
  userHasCompaniesAccess,
  userHasDashboardAccess,
  userHasDataEnrichmentAccess,
  userHasDataEnrichmentLogsAccess,
  userHasFormsAccess,
  userHasICP,
  userHasInternalAccess,
  userHasPeopleAccess,
  userHasSettingsAccess,
  userHasSetupAccess,
} from '~/lib/gates';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { segmentTypes } from '~/lib/segments';

const Audience = lazy(() => import('~/pages/audiences/Show'));
const AudienceEdit = lazy(() => import('~/pages/audiences/Edit'));
const AudienceClone = lazy(() => import('~/pages/audiences/Clone'));
const AudienceNew = lazy(() => import('~/pages/audiences/New'));
const Audiences = lazy(() => import('~/pages/audiences/List'));
const AudiencesAccess = lazy(() => import('~/pages/audiences/Access'));
import ConfigureROIMetrics from '~/pages/audiences/ConfigureROIMetrics';
import ExportSalesPipeline from '~/pages/audiences/ExportSalesPipeline';
const Companies = lazy(() => import('~/pages/companies/List'));
const CompaniesSegment = lazy(() => import('~/pages/companies/Segment'));
import Company from '~/pages/companies/Show';
const Disconnected = lazy(() =>
  import('~/pages/configurations/Show/Disconnected'),
);
const TagsJs = lazy(() => import('~/pages/configurations/Show/TagsJs'));
const Segment = lazy(() => import('~/pages/configurations/Show/Segment'));
const Configurations = lazy(() => import('~/pages/configurations/List'));
const UsageDashboard = lazy(() => import('~/pages/Usage/Dashboard'));
const UsageShow = lazy(() => import('~/pages/Usage/Show'));

const ContactSuccess = lazy(() => import('~/pages/ContactSuccess'));
const IdealCustomerProfile = lazy(() => import('~/pages/IdealCustomerProfile'));
const IdealCustomerProfileNew = lazy(() =>
  import('~/pages/IdealCustomerProfile/New'),
);
const IdealCustomerProfileEdit = lazy(() =>
  import('~/pages/IdealCustomerProfile/Edit'),
);
const VisitorReports = lazy(() => import('~/pages/VisitorReport/List'));
const VisitorReport = lazy(() => import('~/pages/VisitorReport/Show'));

import ImportCsv from '~/pages/CsvImport';
import MapCsv from '~/pages/CsvImport/Map';
const Dashboard = lazy(() => import('~/pages/Dashboard'));
import NewsDetail from '~/pages/NewsDetail';
import NoAccess from '~/pages/NoAccess';
const People = lazy(() => import('~/pages/people/List'));
const PeopleSegment = lazy(() => import('~/pages/people/Segment'));
import Person from '~/pages/people/Show';
const Sandbox = lazy(() => import('~/pages/Sandbox'));
import SegmentDestinationSkeleton from '~/pages/destinations/destinationConfigs/SkeletonForm';
import SegmentDestinationEdit from '~/pages/destinations/Edit';
import SegmentDestinations from '~/pages/destinations/List';
import SegmentDestinationNew from '~/pages/destinations/New';
import EnrichmentAccess from '~/pages/enrichment/Access';
import EnrichmentList from '~/pages/enrichment/List';
import EnrichmentLogs from '~/pages/enrichment/Logs';
import EnrichmentLog from '~/pages/enrichment/LogsShow';
import EnrichmentShow from '~/pages/enrichment/Show';
import FormsAccess from '~/pages/forms/Access';
import FormsList from '~/pages/forms/List';
import FormsShow from '~/pages/forms/Show';
const SegmentEdit = lazy(() => import('~/pages/SegmentEdit'));
const SegmentNew = lazy(() => import('~/pages/SegmentNew'));
const Welcome = lazy(() => import('~/pages/Welcome'));
const Onboarding = lazy(() => import('~/pages/onboarding'));

const ConfigurationPanelFactory = lazy(() =>
  import('~/pages/configurations/Show/ConfigurationPanelFactory'),
);

import { CustomObjects } from '~/pages/enrichment/CustomObjects';
import { LegacyICPRedirect } from '~/pages/IdealCustomerProfile/components/LegacyICPRedirect';

import styles from './App.style';
import Gatekeeper from './app/Gatekeeper';
import Sidebar from './app/Sidebar';
import theme from './app/theme';
import ErrorHandler from './errorHandler';
import GatedRoute from './GatedRoute';
import ImpersonationWarning from './ImpersonationWarning';
import LoadingArea from './LoadingArea';

const UUIDRegex =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const queryClient = new QueryClient();
const concatEnrichmentTypes = integrationTypes.join('|');

class App extends React.Component {
  render() {
    const { classes } = this.props;

    return (
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={apollo}>
          <Provider>
            <Helmet defaultTemplate="Clearbit" titleTemplate="%s – Clearbit" />

            <CKIconGradient />
            <ToastStack />

            <Router history={history}>
              <MuiThemeProvider theme={theme}>
                <Gatekeeper />
                <NPS />

                <div className={classes.root}>
                  <ImpersonationWarning />
                  <Sidebar />

                  <ErrorHandler>
                    <article className={classes.article}>
                      <GatedRoute
                        gateFunction={userHasPeopleAccess}
                        path={[`/people/segments/:id(${UUIDRegex})`, '/people']}
                        render={({
                          match: {
                            params: { id },
                          },
                        }) => (
                          <SegmentsMenu
                            key="person"
                            segmentId={id}
                            type="Person"
                          />
                        )}
                      />
                      <GatedRoute
                        gateFunction={userHasCompaniesAccess}
                        path={[
                          `/companies/segments/:id(${UUIDRegex})`,
                          '/companies',
                        ]}
                        render={({
                          match: {
                            params: { id },
                          },
                        }) => (
                          <SegmentsMenu
                            key="company"
                            segmentId={id}
                            type="Company"
                          />
                        )}
                      />

                      <Suspense fallback={<LoadingArea loading />}>
                        <Switch>
                          <Route component={NoAccess} exact path="/no-access" />

                          <GatedRoute
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path={['/companies/import']}
                            render={() => (
                              <ImportCsv key="Company" type="Company" />
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasPeopleAccess}
                            path={['/people/import']}
                            render={() => (
                              <ImportCsv key="Person" type="Person" />
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path={[`/companies/import/:id(${UUIDRegex})`]}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => (
                              <MapCsv id={id} key="Company" type="Company" />
                            )}
                          />
                          <GatedRoute
                            exact
                            gateFunction={userHasPeopleAccess}
                            path={[`/people/import/:id(${UUIDRegex})`]}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <MapCsv id={id} key="Person" type="Person" />}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            path={[
                              '/audiences',
                              '/audiences/configure-roi-metrics',
                            ]}
                            render={({ match }) => {
                              const showDrawer = match.path.includes(
                                '/configure-roi-metrics',
                              );
                              return (
                                <>
                                  <SidebarDrawer
                                    isVisible={showDrawer}
                                    onToggle={() => history.push('/audiences')}
                                    size="xl"
                                  >
                                    <ConfigureROIMetrics />
                                  </SidebarDrawer>
                                  <Audiences />
                                </>
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            path={[
                              '/audiences',
                              '/audiences/export-sales-pipeline',
                            ]}
                            render={({ match }) => {
                              const showDrawer = match.path.includes(
                                '/export-sales-pipeline',
                              );
                              return (
                                <>
                                  <SidebarDrawer
                                    isVisible={showDrawer}
                                    onToggle={() => history.push('/audiences')}
                                    size="xl"
                                  >
                                    <ExportSalesPipeline />
                                  </SidebarDrawer>
                                  <Audiences />
                                </>
                              );
                            }}
                          />

                          <Route
                            path={[
                              '/audiences/access/request',
                              '/audiences/access',
                            ]}
                            render={({ match: { path } }) => (
                              <TransitionGroup className="w-full">
                                <Transition
                                  key={path}
                                  timeout={500}
                                  unmountOnExit
                                >
                                  {(state) => (
                                    <>
                                      <SidebarDrawer
                                        isVisible={
                                          path.includes('/request') &&
                                          state === 'entered'
                                        }
                                        onToggle={() => {
                                          history.push(paths.audiencesAccess());
                                        }}
                                      >
                                        <ContactSuccess
                                          backPath={paths.audiencesAccess()}
                                          product="advertising"
                                        />
                                      </SidebarDrawer>
                                    </>
                                  )}
                                </Transition>
                                <AudiencesAccess />
                              </TransitionGroup>
                            )}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            path="/audiences/new/:audienceSubtype(company-segment-prospects|contacts|managed-visitors|prospects|visitors)?/:step(sync)?"
                            render={({
                              match: {
                                params: { audienceSubtype, step },
                              },
                              location,
                            }) => {
                              const {
                                segmentId,
                                segmentType,
                              } = queryString.parse(location.search);

                              const stepIndex = [audienceSubtype, step].filter(
                                Boolean,
                              ).length;

                              return (
                                <AudienceNew
                                  key={audienceSubtype}
                                  segmentId={segmentId}
                                  segmentType={segmentType}
                                  stepIndex={stepIndex}
                                  subtype={audienceSubtype?.replace(/-/g, '_')}
                                />
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            path={`/audiences/:id(${UUIDRegex})`}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <Audience id={id} />}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            path="/audiences/:id/edit"
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <AudienceEdit id={id} />}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.audiencesAccess()}
                            gateFunction={userHasAudiencesAccess}
                            path="/audiences/:id/clone/:step(sync)?"
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <AudienceClone id={id} />}
                          />

                          <GatedRoute
                            component={Companies}
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path={`/companies/:companyId(${UUIDRegex})?`}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path="/companies/segments/new"
                            render={({
                              history: {
                                location: { state = {} },
                              },
                            }) => {
                              const { collection_id: collectionId } = state;

                              return (
                                <SegmentNew
                                  collectionId={collectionId}
                                  key={collectionId}
                                  type="Company"
                                />
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path={`/companies/segments/:id(${UUIDRegex})/edit`}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => (
                              <SegmentEdit
                                attributeType="Company"
                                collectionType="Company"
                                id={id}
                                legacyType="Company"
                                type={segmentTypes.company}
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path={[
                              `/companies/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})?/:action?`,
                              `/companies/segments/:segmentId(${UUIDRegex})/destinations/:action?/:product?`,
                            ]}
                            render={({
                              match: {
                                params: { action, id, product, segmentId },
                              },
                              location,
                            }) => {
                              const searchParams = new URLSearchParams(
                                location.search,
                              );

                              return (
                                <>
                                  <SegmentDestinations
                                    segmentId={segmentId}
                                    segmentType={segmentTypes.company}
                                  />
                                  <SidebarDrawer
                                    isVisible={!!action}
                                    onToggle={() => {
                                      history.push(
                                        paths.segmentDestinations(
                                          segmentTypes.company,
                                          segmentId,
                                        ),
                                      );
                                    }}
                                    size="xxl"
                                  >
                                    {(() => {
                                      switch (action) {
                                        case 'new':
                                          return (
                                            <SegmentDestinationNew
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.company}
                                              type={searchParams.get('type')}
                                            />
                                          );
                                        case 'edit':
                                          return (
                                            <SegmentDestinationEdit
                                              destinationId={id}
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.company}
                                            />
                                          );
                                        case 'contact-success':
                                          return (
                                            <ContactSuccess
                                              backPath={paths.segmentDestinations(
                                                segmentTypes.company,
                                                segmentId,
                                              )}
                                              product={product}
                                            />
                                          );
                                        default:
                                          // A default is needed when the segment is animating in and out
                                          return (
                                            <SegmentDestinationSkeleton
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.company}
                                            />
                                          );
                                      }
                                    })()}
                                  </SidebarDrawer>
                                </>
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasCompaniesAccess}
                            path={`/companies/segments/:id(${UUIDRegex})/:personId(${UUIDRegex})?`}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <CompaniesSegment id={id} key={id} />}
                          />

                          <Redirect
                            exact
                            from={`/companies/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})`}
                            to="/companies/segments/:segmentId/destinations/:id/edit"
                          />

                          <GatedRoute
                            component={People}
                            exact
                            gateFunction={userHasPeopleAccess}
                            path={`/people/:personId(${UUIDRegex})?`}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasPeopleAccess}
                            path="/people/segments/new"
                            render={({
                              history: {
                                location: { state = {} },
                              },
                            }) => {
                              const { collection_id: collectionId } = state;

                              return (
                                <SegmentNew
                                  collectionId={collectionId}
                                  key={collectionId}
                                  type="Person"
                                />
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasPeopleAccess}
                            path={`/people/segments/:id(${UUIDRegex})/edit`}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => (
                              <SegmentEdit
                                attributeType="Person"
                                collectionType="Person"
                                id={id}
                                legacyType="Person"
                                type={segmentTypes.person}
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasPeopleAccess}
                            path={[
                              `/people/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})?/:action?`,
                              `/people/segments/:segmentId(${UUIDRegex})/destinations/:action?/:product`,
                            ]}
                            render={({
                              match: {
                                params: { action, id, product, segmentId },
                              },
                              location,
                            }) => {
                              const searchParams = new URLSearchParams(
                                location.search,
                              );

                              return (
                                <>
                                  <SegmentDestinations
                                    segmentId={segmentId}
                                    segmentType={segmentTypes.person}
                                  />
                                  <SidebarDrawer
                                    isVisible={!!action}
                                    onToggle={() =>
                                      history.push(
                                        paths.segmentDestinations(
                                          segmentTypes.person,
                                          segmentId,
                                        ),
                                      )
                                    }
                                    size="xl"
                                  >
                                    {(() => {
                                      switch (action) {
                                        case 'new':
                                          return (
                                            <SegmentDestinationNew
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.person}
                                              type={searchParams.get('type')}
                                            />
                                          );
                                        case 'edit':
                                          return (
                                            <SegmentDestinationEdit
                                              destinationId={id}
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.person}
                                            />
                                          );
                                        case 'contact-success':
                                          return (
                                            <ContactSuccess
                                              backPath={paths.segmentDestinations(
                                                segmentTypes.person,
                                                segmentId,
                                              )}
                                              product={product}
                                            />
                                          );
                                        default:
                                          // A default is needed when the segment is animating in and out
                                          return (
                                            <SegmentDestinationSkeleton
                                              segmentId={segmentId}
                                              segmentType={segmentTypes.person}
                                            />
                                          );
                                      }
                                    })()}
                                  </SidebarDrawer>
                                </>
                              );
                            }}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasPeopleAccess}
                            path={`/people/segments/:id(${UUIDRegex})/:personId(${UUIDRegex})?`}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <PeopleSegment id={id} key={id} />}
                          />

                          <Redirect
                            exact
                            from={`/people/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})`}
                            to="/people/segments/:segmentId/destinations/:id/edit"
                          />

                          <GatedRoute
                            gateFunction={userHasSettingsAccess}
                            path="/settings/configuration/tagsjs"
                          >
                            <TagsJs />
                          </GatedRoute>

                          <GatedRoute
                            gateFunction={userHasSettingsAccess}
                            path="/settings/configuration/segment"
                          >
                            <Segment />
                          </GatedRoute>

                          <GatedRoute
                            gateFunction={userHasSettingsAccess}
                            path="/settings/configuration/:service/connect"
                            render={({
                              match: {
                                params: { service },
                              },
                            }) => <Disconnected service={service} />}
                          />

                          <GatedRoute
                            gateFunction={userHasSettingsAccess}
                            path="/settings/configuration/:service"
                            render={({
                              match: {
                                params: { service },
                              },
                            }) => (
                              <ConfigurationPanelFactory config={service} />
                            )}
                          />

                          <GatedRoute
                            component={Configurations}
                            gateFunction={userHasSettingsAccess}
                            path="/settings/configuration"
                          />

                          <Route
                            component={UsageDashboard}
                            exact
                            path="/settings/usage"
                          />

                          <Route
                            path="/settings/usage/:id"
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <UsageShow id={id} />}
                          />

                          <GatedRoute
                            component={LegacyICPRedirect}
                            gateFunction={userHasICP}
                            path={'/ideal-customer-profile'}
                          />

                          <GatedRoute
                            component={IdealCustomerProfileNew}
                            exact
                            gateFunction={userHasICP}
                            path={paths.idealCustomerProfileNew()}
                          />

                          <GatedRoute
                            component={IdealCustomerProfileEdit}
                            exact
                            gateFunction={userHasICP}
                            path={paths.idealCustomerProfileEdit()}
                          />

                          <GatedRoute
                            component={IdealCustomerProfile}
                            gateFunction={userHasICP}
                            path={paths.idealCustomerProfile()}
                          />

                          <GatedRoute
                            component={Welcome}
                            exact
                            gateFunction={userHasSetupAccess}
                            path="/welcome"
                          />

                          <Route component={Onboarding} path="/onboarding" />

                          <GatedRoute
                            component={Dashboard}
                            exact
                            gateFunction={userHasDashboardAccess}
                            path={['/', '/news/:id']}
                          />

                          <GatedRoute
                            component={Sandbox}
                            gateFunction={userHasInternalAccess}
                            path="/sandbox/:component?"
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.dataEnrichmentAccess()}
                            gateFunction={userHasDataEnrichmentAccess}
                            path="/enrichment"
                            render={() => <EnrichmentList />}
                          />

                          <Route
                            path={[
                              '/enrichment/access/request',
                              '/enrichment/access',
                            ]}
                            render={({ match: { path } }) => (
                              <TransitionGroup className="w-full">
                                <Transition
                                  key={path}
                                  timeout={500}
                                  unmountOnExit
                                >
                                  {(state) => (
                                    <>
                                      <SidebarDrawer
                                        isVisible={
                                          path.includes('/request') &&
                                          state === 'entered'
                                        }
                                        onToggle={() => {
                                          history.push(
                                            paths.dataEnrichmentAccess(),
                                          );
                                        }}
                                      >
                                        <ContactSuccess
                                          backPath={paths.dataEnrichmentAccess()}
                                          product="data_enrichment"
                                        />
                                      </SidebarDrawer>
                                    </>
                                  )}
                                </Transition>
                                <EnrichmentAccess />
                              </TransitionGroup>
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasDataEnrichmentLogsAccess}
                            path={[
                              `/enrichment/:integration(${concatEnrichmentTypes})/logs`,
                              `/enrichment/:integration(${concatEnrichmentTypes})/logs/:id(${UUIDRegex})`,
                            ]}
                            render={({
                              match: {
                                params: { integration },
                              },
                            }) => (
                              <EnrichmentLogs
                                enrichmentLogsType={integration}
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasDataEnrichmentAccess}
                            path={[
                              `/enrichment/:integration(${concatEnrichmentTypes})/custom-objects`,
                              //`/enrichment/:integration(${concatEnrichmentTypes})/custom-objects/:id`,
                            ]}
                            render={({
                              match: {
                                params: { integration },
                              },
                            }) => <CustomObjects integration={integration} />}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasDataEnrichmentAccess}
                            path={[
                              `/enrichment/:integration(${concatEnrichmentTypes})`,
                              `/enrichment/:integration(${concatEnrichmentTypes})/:objectType`,
                            ]}
                            render={({
                              match: {
                                params: { integration, objectType },
                              },
                            }) => (
                              <EnrichmentShow
                                integration={integration}
                                objectType={objectType}
                              />
                            )}
                          />

                          <GatedRoute
                            exact
                            fallbackPath={paths.formsAccess()}
                            gateFunction={userHasFormsAccess}
                            path="/forms"
                            render={() => <FormsList />}
                          />

                          <Route
                            path={['/forms/access/request', '/forms/access']}
                            render={({ match: { path } }) => (
                              <TransitionGroup className="w-full">
                                <Transition
                                  key={path}
                                  timeout={500}
                                  unmountOnExit
                                >
                                  {(state) => (
                                    <>
                                      <SidebarDrawer
                                        isVisible={
                                          path.includes('/request') &&
                                          state === 'entered'
                                        }
                                        onToggle={() => {
                                          history.push(paths.formsAccess());
                                        }}
                                      >
                                        <ContactSuccess
                                          backPath={paths.formsAccess()}
                                          product="form_shortening"
                                        />
                                      </SidebarDrawer>
                                    </>
                                  )}
                                </Transition>
                                <FormsAccess />
                              </TransitionGroup>
                            )}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasFormsAccess}
                            path="/forms/:integration"
                            render={({
                              match: {
                                params: { integration },
                              },
                            }) => <FormsShow integration={integration} />}
                          />

                          <GatedRoute
                            exact
                            gateFunction={userHasFormsAccess}
                            path="/forms/:integration/:selectedTab"
                            render={({
                              match: {
                                params: { integration, selectedTab },
                              },
                            }) => (
                              <FormsShow
                                integration={integration}
                                selectedTab={selectedTab}
                              />
                            )}
                          />

                          <Route
                            path={'/visitor-report/:id'}
                            render={({
                              match: {
                                params: { id },
                              },
                            }) => <VisitorReport id={id} />}
                          />

                          <Route
                            component={VisitorReports}
                            path={'/visitor-report'}
                          />

                          <Route>
                            <MissingPage />
                          </Route>
                        </Switch>
                      </Suspense>

                      {/* Sidebars */}
                      <Switch>
                        <Route
                          path={[
                            `/companies/segments/:segmentId/:id(${UUIDRegex})`,
                            `/people/segments/:segmentId/:id(${UUIDRegex})`,
                            `/companies/:id(${UUIDRegex})`,
                            `/enrichment/:integration(${concatEnrichmentTypes})/logs/:id(${UUIDRegex})`,
                            `/people/:id(${UUIDRegex})`,
                            `/people-new/:id(${UUIDRegex})`,
                            '/companies',
                            `/enrichment/:integration(${concatEnrichmentTypes})/logs`,
                            '/people',
                          ]}
                          render={({ location, match }) => (
                            <TransitionGroup>
                              <Transition
                                key={match.path}
                                timeout={500}
                                unmountOnExit
                              >
                                {(state) => (
                                  <ErrorHandler>
                                    <Switch location={location}>
                                      <GatedRoute
                                        exact
                                        gateFunction={userHasCompaniesAccess}
                                        path={[
                                          `/companies/:id(${UUIDRegex})`,
                                          `/companies/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                        ]}
                                        render={({
                                          match: {
                                            params: { id, segmentId },
                                          },
                                        }) => (
                                          <SidebarDrawer
                                            isVisible={state === 'entered'}
                                            onToggle={() => {
                                              history.push(
                                                paths.segment(
                                                  segmentTypes.company,
                                                  segmentId,
                                                ),
                                              );
                                            }}
                                            size="xxl"
                                          >
                                            <Company id={id} />
                                          </SidebarDrawer>
                                        )}
                                      />

                                      <GatedRoute
                                        exact
                                        gateFunction={
                                          userHasDataEnrichmentLogsAccess
                                        }
                                        path={[
                                          `/enrichment/:integration(${concatEnrichmentTypes})/logs/:id(${UUIDRegex})`,
                                        ]}
                                        render={({
                                          match: {
                                            params: { integration, id },
                                          },
                                        }) => (
                                          <SidebarDrawer
                                            isVisible={state === 'entered'}
                                            onToggle={() => {
                                              history.push(
                                                paths.dataEnrichmentLogs(
                                                  integration,
                                                ),
                                              );
                                            }}
                                          >
                                            <EnrichmentLog
                                              id={id}
                                              type={integration}
                                            />
                                          </SidebarDrawer>
                                        )}
                                      />

                                      <GatedRoute
                                        exact
                                        gateFunction={userHasPeopleAccess}
                                        path={[
                                          `/people/:id(${UUIDRegex})`,
                                          `/people/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                        ]}
                                        render={({
                                          match: {
                                            params: { id, segmentId },
                                          },
                                        }) => (
                                          <SidebarDrawer
                                            isVisible={state === 'entered'}
                                            onToggle={() => {
                                              history.push(
                                                paths.segment(
                                                  segmentTypes.person,
                                                  segmentId,
                                                ),
                                              );
                                            }}
                                            size="xxl"
                                          >
                                            <Person id={id} />
                                          </SidebarDrawer>
                                        )}
                                      />
                                    </Switch>
                                  </ErrorHandler>
                                )}
                              </Transition>
                            </TransitionGroup>
                          )}
                        />

                        <GatedRoute
                          exact
                          gateFunction={userHasDashboardAccess}
                          path={['/news/:id', '/']}
                          render={({
                            match: {
                              path,
                              params: { id },
                            },
                          }) => (
                            <TransitionGroup>
                              <Transition
                                key={path}
                                timeout={500}
                                unmountOnExit
                              >
                                {(state) => (
                                  <SidebarDrawer
                                    isVisible={!!id && state === 'entered'}
                                    onToggle={() => {
                                      history.push('/');
                                    }}
                                  >
                                    <NewsDetail id={id} />
                                  </SidebarDrawer>
                                )}
                              </Transition>
                            </TransitionGroup>
                          )}
                        />
                      </Switch>
                    </article>
                  </ErrorHandler>
                </div>
              </MuiThemeProvider>
              <Route
                path="/"
                render={() => {
                  if (
                    window.analytics &&
                    typeof window.analytics.page === 'function'
                  ) {
                    window.analytics.page(history.location.pathname);
                  }
                  return null;
                }}
              />
            </Router>
          </Provider>
        </ApolloProvider>
      </QueryClientProvider>
    );
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
};

const StyledApp = withStyles(styles)(App);
export default StyledApp;
